import { bindActionCreators, type Dispatch } from "@reduxjs/toolkit";
import { type FC, useCallback, useEffect, useMemo, useState } from "react";
import { connect, type ConnectedProps } from "react-redux";

import { type QueryObserverSuccessResult } from "@tanstack/react-query";
import { ReportUnavailable } from "components/reportUnavailable/ReportUnavailable";
import {
  chartColorScale,
  chartLegendLabels,
  type FormattedLineData,
  noBarData,
  noData,
  validLineData,
  dailyActivity,
  lineChartTitles,
  totalActivity,
  type PerformanceWithAccountFilter,
  createDateRange,
  lineChartMargins,
  isDateFilterValid,
} from "features/Library/Common/utils/performanceUtils";
import { EmptyStateWrapper } from "features/Library/Flows/Performance/EmptyStateWrapper";
import {
  generateUserFlowStatistics,
  useFlowEngagementQuery,
  useFlowLineChartQuery,
} from "features/Reporting/Content/queries/useFlowLineChartQuery";
import {
  sharedAccountReportingHorizontalBarProps,
  sharedAccountReportingLineProps,
} from "features/Reporting/Content/shared";
import { setExportAction, setHasDataToExport } from "features/Reporting/state/export/exportSlice";
import { type AssetActivityCounts } from "features/Reporting/types/content";
import { useFeatureFlag } from "hooks/useFeatureFlag";
import { bindAction } from "interfaces";
import CardReporting from "../../../../components/cards/CardReporting/CardReporting";
import { ChartWrapper, getDateFormatByCount, HorizontalBarChart, LineChart } from "../../../../components/charts";
import { type Node } from "../../../../components/charts/types/Sankey";
import { RequestStatusRenderer } from "../../../../components/requestStatsRenderer/RequestStatusRenderer";
import { FeatureFlags } from "../../../../featureFlags";
import { type RootState } from "../../../Application/globaltypes/redux";
import { type RegisterBreadcrumbCallback } from "../../Common/Hooks/usePerformanceBreadcrumbs";
import { selectFlowGoalPerformanceInfo } from "../state/slices/flowGoalSlice";
import {
  selectPeopleDetailsStatus,
  selectPeopleDetailsValue,
  selectUniqueEngagementNodes,
} from "../state/slices/flowPerformanceSlice";
import * as flowPerformanceActions from "../state/thunks/flowPerformanceThunk";
import FlowPerformanceDrilldown from "./Drilldown/FlowPerformanceDrilldown";
import { type AssetInfo } from "./FlowPerformance";
import FlowPerformanceTabs from "./Tabs/FlowPerformanceTabs";
import { useChartPeriodMeasure } from "hooks/useChartPeriodMeasure";

import "../../Common/utils/performanceSCSSUtils.scss";
import "./flowPerformance.scss";
import { RStatus } from "features/Application/globaltypes/fetchRequest";
import { useFlowPriorityPeopleDetailsQuery } from "./Tabs/FlowPrioritizationQueries";

export interface Props extends PropsFromRedux {
  flowId: number;
  flowTitle: string;
  registerBreadcrumb: RegisterBreadcrumbCallback;
  dateFilter: PerformanceWithAccountFilter | null;
  setSelectedAssetFlow?: (content: AssetInfo | undefined) => void;
  includeAccounts?: boolean;
  canNotify?: boolean;
  appliedStatus?: number[];
  childAccountId?: number;
  hasCreateAccountsAddOn?: boolean;
}

const lineData = (
  chartState: string,
  lineChartQuery: QueryObserverSuccessResult<FormattedLineData<AssetActivityCounts>, unknown>,
) => {
  if (chartState === dailyActivity) {
    return {
      xData: [lineChartQuery.data?.Date, lineChartQuery.data?.Date],
      yData: [lineChartQuery.data?.InProgress, lineChartQuery.data?.Completed],
    };
  }
  return {
    xData: [lineChartQuery.data?.Date, lineChartQuery.data?.Date],
    yData: [lineChartQuery.data?.InProgressTotal, lineChartQuery.data?.CompletedTotal],
  };
};

const noCardData = generateUserFlowStatistics(0, 0, 0, 0);

export const FlowPerformanceBody: FC<Props> = ({
  flowId,
  flowTitle,
  registerBreadcrumb,
  dateFilter,
  uniqueSankeyNodes,
  goalInfo,
  actions,
  setExportAction,
  setSelectedAssetFlow,
  includeAccounts,
  canNotify,
  appliedStatus,
  childAccountId,
  hasCreateAccountsAddOn,
  setHasExportData,
  peopleDetailsStatus,
  peopleDetailsData,
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const dateRange = useMemo(() => {
    if (dateFilter === null) return null;
    return createDateRange(dateFilter.dateFrom, dateFilter.dateTo);
  }, [dateFilter]);

  const [chartPeriod, measureRef] = useChartPeriodMeasure(
    dateRange ?? [],
    lineChartMargins.left + lineChartMargins.right,
  );

  const [selectedAsset, setSelectedAsset] = useState<AssetInfo>();
  const [currentAssetIndex, setCurrentAssetIndex] = useState(0);
  const [chartState, setChartState] = useState(totalActivity);
  const reportEnabled = !!useFeatureFlag(FeatureFlags.FlowReport);
  const lineChartQuery = useFlowLineChartQuery(dateFilter, chartPeriod, flowId, dateFilter?.versionNumber);
  const engagementQuery = useFlowEngagementQuery(dateFilter, flowId, dateFilter?.versionNumber);
  const shouldSendRequests = reportEnabled && dateFilter !== null;
  const peopleQuery = useFlowPriorityPeopleDetailsQuery({ ...dateFilter! }, flowId);

  useEffect(() => {
    if (setSelectedAssetFlow !== undefined) setSelectedAssetFlow(selectedAsset);
  }, [setSelectedAssetFlow, selectedAsset]);

  useEffect(() => {
    // Should only fetch when endpoints are available (and date range is required)
    if (shouldSendRequests && dateFilter.type !== undefined && dateFilter.versionNumber !== null) {
      actions.fetchEngagementData(flowId, dateFilter);
      actions.fetchPeopleDetails(flowId, dateFilter);
      // No dates to filter, so set default values so cards / sankey appear
      // in their "empty" states
    } else {
      actions.setDefaultValues();
    }
  }, [actions, flowId, dateFilter, shouldSendRequests]);

  useEffect(() => {
    setHasExportData({
      hasDataToExport:
        (peopleDetailsStatus === RStatus.Got && !!peopleDetailsData.length) ||
        (peopleQuery.isSuccess && !!peopleQuery.data.length),
      tooltipContent: "No data available for export. This Flow contains no data.",
    });
  }, [peopleDetailsData, setHasExportData, peopleDetailsStatus, peopleQuery.data?.length, peopleQuery.isSuccess]);

  useEffect(() => {
    if (shouldSendRequests && goalInfo && activeTab === 1) {
      const { includeMyData, accounts, status, ...restOfFilter } = dateFilter;

      // Remove 0 and 3 from status, as they are unsupported by the API.
      const filteredStatus = status?.filter((s) => s !== 0 && s !== 3);

      actions.fetchGoalsLineChart(flowId, { ...restOfFilter, status: filteredStatus }, goalInfo);
      actions.fetchGoalsTotals(flowId, { ...restOfFilter, status: filteredStatus }, goalInfo);
      actions.fetchGoalsCards(flowId, { ...restOfFilter, status: filteredStatus }, goalInfo);
    }
  }, [actions, dateFilter, flowId, goalInfo, shouldSendRequests, activeTab]);

  const getSankeyNodeIndex = useCallback(
    (currentNodeId: number): number => uniqueSankeyNodes.findIndex((value) => value.id === currentNodeId),
    [uniqueSankeyNodes],
  );

  const handleNodeTitleClick = useCallback(
    (node: Node) => {
      setSelectedAsset({ id: node.id!, type: node.type, name: node.name });
      setCurrentAssetIndex(getSankeyNodeIndex(node.id!));
    },
    [getSankeyNodeIndex],
  );

  const handleReset = useCallback(() => {
    setSelectedAsset(undefined);
  }, []);

  // Setting up correct export method if send entity is changed
  useEffect(() => {
    if (!selectedAsset) {
      setExportAction({
        method: actions.handleFlowExport,
        args: [flowId, dateFilter, flowTitle],
        isExportEnabled: true,
      });
    }
  }, [actions.handleFlowExport, dateFilter, flowId, setExportAction, selectedAsset, flowTitle]);

  const handleNextAsset = useCallback(() => {
    const currentIdx = getSankeyNodeIndex(selectedAsset!.id);
    const wrappedIndex = (currentIdx + 1) % uniqueSankeyNodes.length;
    setSelectedAsset(uniqueSankeyNodes[wrappedIndex]);
    setCurrentAssetIndex(wrappedIndex);
  }, [getSankeyNodeIndex, selectedAsset, uniqueSankeyNodes]);

  const handlePrevAsset = useCallback(() => {
    const currentIdx = getSankeyNodeIndex(selectedAsset!.id);
    const wrappedIndex = (currentIdx - 1 + uniqueSankeyNodes.length) % uniqueSankeyNodes.length;

    setSelectedAsset(uniqueSankeyNodes[wrappedIndex]);
    setCurrentAssetIndex(wrappedIndex);
  }, [getSankeyNodeIndex, selectedAsset, uniqueSankeyNodes]);

  const renderRoot = useMemo(
    () => (
      <div className="flowBody" data-testid="flow performance body root">
        <div className="chartsGraphs">
          <div className="lineChartContainer" ref={measureRef}>
            <ChartWrapper
              titles={lineChartTitles}
              showLegend
              legendLabels={chartLegendLabels}
              colorRange={chartColorScale}
              onChange={setChartState}
            >
              <EmptyStateWrapper dateFilter={dateFilter} emptyState={noData(isDateFilterValid(dateFilter))}>
                <RequestStatusRenderer state={lineChartQuery.status}>
                  {lineChartQuery.isSuccess &&
                  validLineData([
                    lineChartQuery.data.Completed,
                    lineChartQuery.data.CompletedTotal,
                    lineChartQuery.data.InProgress,
                    lineChartQuery.data.InProgressTotal,
                  ]) ? (
                    <LineChart
                      {...sharedAccountReportingLineProps}
                      xFormatterFunc={getDateFormatByCount(lineChartQuery.data.Date.length)}
                      colorRange={chartColorScale}
                      {...lineData(chartState, lineChartQuery)}
                    />
                  ) : (
                    noData(isDateFilterValid(dateFilter))
                  )}
                </RequestStatusRenderer>
              </EmptyStateWrapper>
            </ChartWrapper>
          </div>
          <div className="funnelChartContainer">
            <ChartWrapper titles={["Engagement"]}>
              <EmptyStateWrapper dateFilter={dateFilter} emptyState={noData(isDateFilterValid(dateFilter))}>
                <RequestStatusRenderer state={engagementQuery.status}>
                  {engagementQuery.isSuccess && !noBarData(...engagementQuery.data.barData.map((d) => d.value)) ? (
                    <HorizontalBarChart
                      {...sharedAccountReportingHorizontalBarProps}
                      data={engagementQuery.data.barData}
                    />
                  ) : (
                    noData(isDateFilterValid(dateFilter))
                  )}
                </RequestStatusRenderer>
              </EmptyStateWrapper>
            </ChartWrapper>
          </div>
        </div>
        <div className="cardSection">
          <EmptyStateWrapper dateFilter={dateFilter} emptyState={<CardReporting items={noCardData} />}>
            <RequestStatusRenderer state={engagementQuery.status}>
              {engagementQuery.isSuccess && <CardReporting items={engagementQuery.data.cardDataIndividual} />}
            </RequestStatusRenderer>
          </EmptyStateWrapper>
        </div>
        <FlowPerformanceTabs
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          includeAccounts={includeAccounts}
          childAccountId={childAccountId}
          hasCreateAccountsAddOn={hasCreateAccountsAddOn}
          onClick={handleNodeTitleClick}
          dateFilter={dateFilter}
          flowId={flowId}
          canNotify={canNotify}
          appliedStatus={appliedStatus}
        />
      </div>
    ),
    [
      measureRef,
      dateFilter,
      lineChartQuery,
      chartState,
      engagementQuery.status,
      engagementQuery.isSuccess,
      engagementQuery.data?.barData,
      engagementQuery.data?.cardDataIndividual,
      activeTab,
      includeAccounts,
      childAccountId,
      hasCreateAccountsAddOn,
      handleNodeTitleClick,
      flowId,
      canNotify,
      appliedStatus,
    ],
  );

  if (!reportEnabled) {
    return <ReportUnavailable />;
  }

  return selectedAsset ? (
    <FlowPerformanceDrilldown
      flowId={flowId}
      assetId={selectedAsset.id}
      assetType={selectedAsset.type}
      assetName={selectedAsset.name}
      dateFilter={dateFilter!}
      onReset={handleReset}
      registerBreadcrumb={registerBreadcrumb}
      onNextAsset={handleNextAsset}
      onPrevAsset={handlePrevAsset}
      currentAssetIndex={currentAssetIndex}
      totalAssetsCount={uniqueSankeyNodes.length}
    />
  ) : (
    renderRoot
  );
};

/* istanbul ignore next */
const mapStateToProps = (state: RootState) => {
  return {
    peopleDetailsStatus: selectPeopleDetailsStatus(state),
    peopleDetailsData: selectPeopleDetailsValue(state),
    uniqueSankeyNodes: selectUniqueEngagementNodes(state),
    goalInfo: selectFlowGoalPerformanceInfo(state),
  };
};

/* istanbul ignore next */
const mapDispatchToProps = (dispatch: Dispatch) => {
  return {
    actions: bindActionCreators(flowPerformanceActions, dispatch),
    setExportAction: bindAction(setExportAction, dispatch),
    setHasExportData: bindAction(setHasDataToExport, dispatch),
  };
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(FlowPerformanceBody);
export default ConnectedComponent;
